import { useRef, useState } from "react";
// @mui
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { RouterLink } from "src/routes/components";
import { LoadingButton } from "@mui/lab";

// components
import Iconify from "src/components/iconify";
import PropTypes from "prop-types";

// Utils
import { useLocales } from "src/locales";
import { useFormContext } from "react-hook-form";

// ----------------------------------------------------------------------

function SplitButton({ options, disabled, loading }) {
  const { t } = useLocales();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  if (!options?.length) return null;

  const primary = options[0];
  const color = primary.color || "warning";

  return (
    <>
      <Box sx={{ display: "inline-flex" }}>
        <LoadingButton
          onClick={primary.onClick}
          variant="soft"
          color={color}
          disabled={disabled}
          loading={loading}
          startIcon={<Iconify icon={primary.icon || "ep:finished"} />}
          sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        >
          {primary.label}
        </LoadingButton>
        <LoadingButton
          ref={anchorRef}
          variant="soft"
          color={color}
          disabled={disabled || loading}
          onClick={() => setOpen(prev => !prev)}
          aria-label={t("moreOptions")}
          sx={{ px: 1, minWidth: 0, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        >
          <Iconify icon="mingcute:down-line" />
        </LoadingButton>
      </Box>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {options.map(option => (
          <MenuItem
            key={option.key}
            disabled={option.disabled}
            onClick={() => {
              setOpen(false);
              option.onClick?.();
            }}
          >
            <Iconify
              icon={option.icon}
              sx={{ mr: 1 }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

SplitButton.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      icon: PropTypes.string,
      color: PropTypes.string,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
    })
  ),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};

export default function Submit({
  showCancel = true,
  margin = "10px",
  showSave = true,
  back = false,
  saveIcon = "material-symbols:save-outline",
  saveDisabled = false,
  ...props
}) {
  const { t } = useLocales();
  const cancelLabel = t(back ? "back" : "cancel");
  const isRunning = useRef(false);
  const [isSubmittingState, setIsSubmitting] = useState(false);

  const { formState: { isSubmitting } = {} } = useFormContext() ?? {};

  const onClick = () => {
    if (props.onClick) props.onClick();
    else window.history.back();
  };

  const onClickSaveDefault = async () => {
    if (isRunning.current) return;
    isRunning.current = true;
    setIsSubmitting(true);
    try {
      await props.onClickSave();
    } catch (error) {
    } finally {
      isRunning.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    // Los botones se separan con gap y no se agrupan: ButtonGroup está pensado para botones pegados,
    // les cuadra las esquinas internas y les agrega un borde divisorio.
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 2,
        m: margin,
        "&&": { marginTop: margin },
      }}
    >
      {showCancel ? (
        <Tooltip title={cancelLabel}>
          {props.pathCancel ? ( // cuando tiene component refresca siempre
            <LoadingButton
              component={RouterLink}
              variant="soft"
              color="error"
              href={props.pathCancel}
              startIcon={<Iconify icon="ep:back" />}
            >
              {cancelLabel}
            </LoadingButton>
          ) : (
            <LoadingButton
              onClick={onClick}
              variant="soft"
              color="error"
              startIcon={<Iconify icon="ep:back" />}
            >
              {cancelLabel}
            </LoadingButton>
          )}
        </Tooltip>
      ) : null}

      {showSave && props?.entity?.canEdit !== false ? (
        <>
          {props.onClickSave ? (
            <LoadingButton
              onClick={onClickSaveDefault}
              variant="soft"
              color="primary"
              disabled={saveDisabled || isSubmitting || isSubmittingState}
              loading={isSubmitting || isSubmittingState}
              startIcon={<Iconify icon={saveIcon} />}
            >
              {props.saveText ??
                (!props.entity?.id && props.label
                  ? t("create")
                  : props.labelSaveChange
                  ? props.labelSaveChange
                  : t("saveChanges"))}
            </LoadingButton>
          ) : (
            <LoadingButton
              type="submit"
              variant="soft"
              color="primary"
              disabled={saveDisabled || isSubmitting || isSubmittingState}
              loading={isSubmitting || isSubmittingState}
              startIcon={<Iconify icon={saveIcon} />}
            >
              {props.saveText ??
                (!props.entity?.id && props.label
                  ? t("create")
                  : props.labelSaveChange
                  ? props.labelSaveChange
                  : t("saveChanges"))}
            </LoadingButton>
          )}
        </>
      ) : null}

      {props.saveAndFinish ? (
        <Tooltip title={t("finish")}>
          <LoadingButton
            onClick={props.onClickSaveAndFinish}
            disabled={isSubmitting || isSubmittingState}
            loading={isSubmitting || isSubmittingState}
            variant="soft"
            color="warning"
            startIcon={<Iconify icon="ep:finished" />}
          >
            {t("finish")}
          </LoadingButton>
        </Tooltip>
      ) : null}

      {props.finishMenu?.length ? (
        <SplitButton
          options={props.finishMenu}
          disabled={isSubmitting || isSubmittingState}
          loading={isSubmitting || isSubmittingState}
        />
      ) : null}

      {props.showPlayButton ? (
        <Tooltip title={t("play")}>
          <LoadingButton
            onClick={props.onClickPlayTask}
            disabled={props.playLoading || isSubmitting || isSubmittingState}
            loading={props.playLoading || isSubmitting || isSubmittingState}
            variant="soft"
            color="success"
            startIcon={<Iconify icon="line-md:play-filled" />}
          >
            {t("play")}
          </LoadingButton>
        </Tooltip>
      ) : null}

      {props.showPauseButton ? (
        <Tooltip title={t("stop")}>
          <LoadingButton
            onClick={props.onClickPauseTask}
            disabled={props.pauseLoading || isSubmitting || isSubmittingState}
            loading={props.pauseLoading || isSubmitting || isSubmittingState}
            variant="soft"
            color="error"
            startIcon={<Iconify icon="material-symbols:stop-circle-outline" />}
          >
            {t("stop")}
          </LoadingButton>
        </Tooltip>
      ) : null}

      {props.clearButton ? (
        <Tooltip title={t("clear")}>
          <LoadingButton
            onClick={props.onClickClear}
            disabled={isSubmitting || isSubmittingState}
            loading={isSubmitting || isSubmittingState}
            variant="soft"
            color="warning"
            startIcon={<Iconify icon="fluent-mdl2:clear-filter" />}
          >
            {t("clear")}
          </LoadingButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

Submit.propTypes = {
  label: PropTypes.string,
  labelSaveChange: PropTypes.string,
  entity: PropTypes.any,
  saveAndFinish: PropTypes.bool,
  finishMenu: PropTypes.array,
  showPlayButton: PropTypes.bool,
  showPauseButton: PropTypes.bool,
  showSave: PropTypes.any,
  saveDisabled: PropTypes.bool,
  methods: PropTypes.any,
  onClickSaveAndFinish: PropTypes.any,
  onClickPlayTask: PropTypes.any,
  onClickPauseTask: PropTypes.any,
  onClickSave: PropTypes.any,
  onClick: PropTypes.any,
  clearButton: PropTypes.any,
  onClickClear: PropTypes.any,
  showCancel: PropTypes.any,
  back: PropTypes.bool,
  margin: PropTypes.any,
  pathCancel: PropTypes.string,
  saveIcon: PropTypes.string,
  saveText: PropTypes.string,
  playLoading: PropTypes.bool,
  pauseLoading: PropTypes.bool,
};
